import { ref, computed, watch } from 'vue';
import store from '@/store';
import { config } from '@/config';
import { useToast } from '@/composables/common/useToast';

/**
 * Composable that owns the plazos state for the backoffice
 * `RenovacionRapidaComponent` (paso 2 toggle + paso 3 resumen).
 *
 * NOT YET wired into the component (WU3 of the SDD change). The factory
 * accepts getters instead of snapshots because Options API `setup()` runs
 * before `data()` finishes initializing, so direct `this.abonadoLocal`
 * access is unreliable; getters defer the read until first access.
 *
 * The composable writes plazo results to `abonadoLocal.plazos` and
 * `abonadoLocal.plazos_pendientes` (single source of truth for the renew
 * transport contract) and reads configuration from `abonadoLocal.area`
 * and `abonadoLocal.precio`.
 *
 * @param {Object} args
 * @param {Function} args.getAbonadoLocal - () => abonadoLocal reactive proxy
 * @param {Function} args.getTeatro       - () => teatro object from store
 * @returns {Object} composable surface
 */
export function useRenovacionRapidaPlazos({ getAbonadoLocal, getTeatro } = {}) {
    const toast = useToast();

    const loadingCalculo = ref(false);
    const errorPlazos = ref(false);
    const plazosResult = ref(null);
    const inFlightToken = ref(0);

    const compraConPlazos = computed(() => {
        const abonado = typeof getAbonadoLocal === 'function' ? getAbonadoLocal() : null;
        if (!abonado?.metodo_pago) return false;
        return Number(abonado.metodo_pago.id) === 9;
    });

    const frecuenciasConfig = computed(() => {
        const abonado = typeof getAbonadoLocal === 'function' ? getAbonadoLocal() : null;
        const area = abonado?.area || {};
        return {
            min: area.min_cantidad_plazos,
            max: area.max_cantidad_plazos,
            frecuencias: area.frecuencias || [],
        };
    });

    async function calcularPlazos({ n_plazos, frecuencia }) {
        if (loadingCalculo.value) return;

        const myToken = ++inFlightToken.value;

        const abonado = typeof getAbonadoLocal === 'function' ? getAbonadoLocal() : null;
        const teatro = typeof getTeatro === 'function' ? getTeatro() : null;
        const area = abonado?.area;
        const precio = abonado?.precio;

        if (!area || !precio) return;

        if (
            typeof n_plazos !== 'number'
            || n_plazos < area.min_cantidad_plazos
            || n_plazos > area.max_cantidad_plazos
        ) {
            return;
        }

        const frecuencias = area.frecuencias || [];
        const frecuenciaValida = frecuencias.some(f => f && f.id === frecuencia?.id);
        if (!frecuenciaValida) return;

        loadingCalculo.value = true;
        errorPlazos.value = false;

        try {
            const payload = {
                filters: { tid: teatro?.hash || '' },
                abonado_id: abonado.id,  // substitutes %abonado_id% in route URL
                n_plazos,
                frecuencia,
                precio_total_taquilla: precio.precio_total_taquilla || 0,
                descuento_aplicado: abonado.descuento_aplicado || 0,
                descuento_integro: abonado.descuento_integro || 0,
                precio_lista_abonado: {
                    id: precio.id,
                    precio: precio.precio,
                    min_cantidad_plazos: area.min_cantidad_plazos,
                    max_cantidad_plazos: area.max_cantidad_plazos,
                    frecuencias: area.frecuencias,
                    activar_pago_plazos: area.activar_pago_plazos,
                },
                datos_comprador: {
                    precio: {
                        precio: precio.precio,
                        price_management: precio.price_management,
                        price_management_interno: precio.price_management_interno,
                        price_management_taquilla: precio.price_management_taquilla,
                        price_management_taquilla_interno: precio.price_management_taquilla_interno,
                    },
                },
                fecha_limite_plazos: teatro?.fecha_limite_plazos || null,
            };

            const res = await store.dispatch(
                config.getVuexAlias('back.abonados.calcular.plazos.renovacion'),
                payload,
            );

            if (myToken !== inFlightToken.value) return;

            const data = res?.data || {};
            if (abonado) {
                abonado.plazos = Boolean(data.plazos);
                abonado.plazos_pendientes = data.plazos_pendientes || [];
            }
            plazosResult.value = data;
        } catch (err) {
            if (myToken !== inFlightToken.value) return;
            // eslint-disable-next-line no-console
            console.error(err);
            const status = err?.response?.status;
            if (status === 404) {
                errorPlazos.value = true;
            } else {
                const msg = err?.response?.data?.message || 'Error al calcular los plazos';
                toast.error(msg);
            }
        } finally {
            if (myToken === inFlightToken.value) {
                loadingCalculo.value = false;
            }
        }
    }

    function resetPlazos() {
        inFlightToken.value++;
        const abonado = typeof getAbonadoLocal === 'function' ? getAbonadoLocal() : null;
        if (abonado) {
            abonado.plazos = false;
            abonado.plazos_pendientes = [];
        }
        errorPlazos.value = false;
        plazosResult.value = null;
        loadingCalculo.value = false;
    }

    watch(
        () => {
            const abonado = typeof getAbonadoLocal === 'function' ? getAbonadoLocal() : null;
            return abonado?.metodo_pago?.id;
        },
        (newId, oldId) => {
            if (oldId === 9 && newId !== 9) {
                resetPlazos();
            }
        }
    );

    return {
        loadingCalculo,
        errorPlazos,
        plazosResult,
        compraConPlazos,
        frecuenciasConfig,
        calcularPlazos,
        resetPlazos,
    };
}
